import { z } from 'zod';

/**
 * 저장소 상태 계약 (11회차 결정 4).
 *
 * 저장은 두 층이다. 핫은 `hot/{kind}/{service}/{날짜}/*.ndjson.gz`(flush마다 새 파일),
 * 콜드는 `cold/{kind}/{service}/{날짜}.parquet`(날짜당 한 파일, 추가 변환분이 있으면 여럿).
 * 화면이 "지금 무엇이 어디에 얼마나 있는지"와 "마지막 변환이 어땠는지"를 이것으로 본다.
 *
 * 23-C회차에 경로에 서비스 폴더가 한 칸 늘었다. 그 전에 쓰인 구조(`hot/{kind}/{날짜}/`)도
 * 그대로 읽으므로 `service`가 **없는** 행이 섞여 나온다. 화면은 그것을 "이전 구조"로 적는다.
 *
 * 규칙 5대로 관제용 DB가 없으므로 이 상태는 파일 시스템을 그때그때 훑은 결과다.
 */

const count = z.number().int().nonnegative();
const bytes = z.number().int().nonnegative();

/**
 * 저장 종류. 폴더 이름이다. CPU 프로파일 원본은 변환하지 않아 여기 없다.
 * `workeragg`는 워커 트랜잭션의 1분 집계다 (2026-09-07 결정 1). 개별 요약 행이 없는 대신 이것이 남는다.
 */
export const StorageKind = z.enum(['tx', 'span', 'dbevent', 'alert', 'workeragg']);
export type StorageKind = z.infer<typeof StorageKind>;

export const STORAGE_KIND_LABEL: Record<StorageKind, string> = {
  tx: '요청 요약',
  span: '구간',
  dbevent: 'DB 사건',
  alert: '알림',
  workeragg: '워커 집계',
};

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** 핫에 남아 있는 하루 */
export const HotDay = z.object({
  kind: StorageKind,
  day,
  /** 서비스 폴더 이름. 없으면 23-C회차 전 구조(서비스가 섞여 있다) */
  service: z.string().optional(),
  files: count,
  bytes,
  /** 날짜가 완결돼 지금 변환해도 되는지. 오늘(UTC)이면 거짓이다 */
  convertible: z.boolean(),
});
export type HotDay = z.infer<typeof HotDay>;

/** 콜드로 접힌 하루 */
export const ColdDay = z.object({
  kind: StorageKind,
  day,
  /** 서비스 폴더 이름. 없으면 23-C회차 전 구조(서비스가 섞여 있다) */
  service: z.string().optional(),
  /** 추가 변환분이 있으면 2 이상 */
  files: count,
  bytes,
  /** Parquet 푸터에서 읽은 행 수. 못 읽으면 없다 */
  rows: count.optional(),
});
export type ColdDay = z.infer<typeof ColdDay>;

export const ConvertedDay = z.object({
  kind: StorageKind,
  day,
  /** 서비스 폴더 이름. 23-C회차 전 구조(서비스 폴더 없음)면 없다 */
  service: z.string().optional(),
  rows: count,
  bytes,
  ms: z.number().nonnegative(),
});
export type ConvertedDay = z.infer<typeof ConvertedDay>;

export const FailedDay = z.object({
  kind: StorageKind,
  day,
  service: z.string().optional(),
  reason: z.string(),
});
export type FailedDay = z.infer<typeof FailedDay>;

/** 마지막 변환 배치 한 번의 결과 */
export const StorageRun = z.object({
  at: z.number().int().nonnegative(),
  converted: z.array(ConvertedDay),
  failed: z.array(FailedDay),
});
export type StorageRun = z.infer<typeof StorageRun>;

/** 합친 시간대 하나 (25회차) */
export const CompactedHour = z.object({
  kind: StorageKind,
  day,
  /** 서비스 폴더 이름. 23-C회차 전 구조면 없다 */
  service: z.string().optional(),
  /** 두 자리 시(UTC) */
  hour: z.string().regex(/^[0-2][0-9]$/),
  /** 합친 원본 파일 수 */
  files: count,
  rows: count,
});
export type CompactedHour = z.infer<typeof CompactedHour>;

/**
 * `POST /api/storage/compact` 응답 (25회차).
 * 변환과 같은 모양이다 — 한 번에 여러 시간대가 합쳐질 수 있어 결과가 목록이다.
 */
export const StorageCompactRun = z.object({
  at: z.number().int().nonnegative(),
  compacted: z.array(CompactedHour),
  failed: z.array(FailedDay),
});
export type StorageCompactRun = z.infer<typeof StorageCompactRun>;

/** 지금 물고 있는 보존 설정. 화면이 "왜 지워졌는지"를 설명할 수 있게 함께 내보낸다 */
export const StorageRetention = z.object({
  /** 이 일수가 지난 핫부터 변환한다. 오늘은 언제나 제외 */
  hotDays: count,
  /** 변환에 계속 실패해 이 일수를 넘게 남은 핫은 경고만 하고 둔다 */
  hotMaxDays: count,
  /** 변환 뒤 핫을 지우는지 */
  deleteHot: z.boolean(),
  coldTxDays: count,
  coldSpanDays: count,
  coldDbEventDays: count,
  coldAlertDays: count,
  /** 워커 1분 집계 콜드 보존 일수 (2026-09-07 결정 1) */
  coldWorkerAggDays: count,
  /** CPU 프로파일 원본 보존 일수. 변환 대상이 아니라 여기만 있다 */
  profileDays: count,
  /** 데이터 디렉터리 여유가 이 밑이면 가장 오래된 콜드부터 지운다 */
  diskMinFreeMb: count,
});
export type StorageRetention = z.infer<typeof StorageRetention>;

/**
 * 한 서비스 폴더의 종류별 요약 (23-C회차).
 * 날짜별 표는 `hot`·`cold`에 그대로 있고, 이쪽은 "앱마다 얼마나 쓰고 얼마나 두는가"다.
 */
export const StorageServiceKind = z.object({
  kind: StorageKind,
  hotDays: count,
  hotFiles: count,
  hotBytes: bytes,
  coldDays: count,
  coldFiles: count,
  coldBytes: bytes,
  /** Parquet 푸터에서 읽은 행 수 합. 한 날짜라도 못 읽으면 없다 */
  coldRows: count.optional(),
  /** 이 서비스·종류에 실제로 걸리는 콜드 보존 일수 */
  retentionDays: count,
  /** 전역 기본이 아니라 서비스별 덮어쓰기가 걸렸는지 (`APM_RETENTION_OVERRIDES`) */
  retentionOverride: z.boolean(),
});
export type StorageServiceKind = z.infer<typeof StorageServiceKind>;

/** 서비스 폴더 하나 (23-C회차) */
export const StorageService = z.object({
  /** 폴더 이름. `legacy`면 없다 */
  service: z.string().optional(),
  /**
   * 사람이 읽는 서비스 이름 (26회차). 이번 실행에서 그 폴더로 쓴 적이 있으면 **원문**이고,
   * 아니면 폴더 이름에서 해시를 뗀 접두사다(원문이 아닐 수 있다). `legacy`면 없다
   */
  name: z.string().optional(),
  /** 23-C회차 전 구조라 서비스가 섞여 있다. 서비스별 보존 덮어쓰기가 걸리지 않는다 */
  legacy: z.boolean(),
  hotBytes: bytes,
  coldBytes: bytes,
  /** 자료가 있는 종류만. 오래 쓴 순이 아니라 저장 종류 순이다 */
  kinds: z.array(StorageServiceKind),
});
export type StorageService = z.infer<typeof StorageService>;

/** `GET /api/storage` 응답 */
export const StorageStatus = z.object({
  dataDir: z.string(),
  /** 데이터 디렉터리가 놓인 파일 시스템의 여유. 못 재면 없다 */
  diskFreeMb: z.number().nonnegative().optional(),
  diskTotalMb: z.number().nonnegative().optional(),
  hot: z.array(HotDay),
  cold: z.array(ColdDay),
  /**
   * 서비스 폴더별 요약 (23-C회차). 옛 구조는 `legacy: true`인 항목 하나로 모인다.
   * 23-C회차 전 서버의 응답에는 없어 기본값을 둔다
   */
  services: z.array(StorageService).default([]),
  lastRun: StorageRun.optional(),
  /** 변환이 지금 돌고 있는지. 참이면 화면의 변환 단추를 잠근다 */
  busy: z.boolean(),
  retention: StorageRetention,
});
export type StorageStatus = z.infer<typeof StorageStatus>;
